import React, { PropsWithChildren, useContext, useState } from "react";
import { useRouter } from "next/navigation";
import { AppShell, Badge, Group, Text, useMantineTheme } from "@mantine/core";
import { useColorScheme } from "@mantine/hooks";
import { UserContext } from "../../lib/context";
import { TravelScrollBurger, TravelScrollDrawer } from "../TravelScrollMenu";
import { SheetsMountPoint } from "../Sheets";
import { Guide, Trip } from '../../lib/interfaces';
import { useIsWide } from "../../lib/hooks";

import shellClasses from "./ShellStyles.module.css";
import { UnauthenticatedRightSide } from "./UnauthenticatedRightSide";

export function TSAppShell(props: {
    header?: {
        canOpenMenu?: boolean,
        title?: string,
        rightSide?: JSX.Element | false,
        bottom?: JSX.Element | false,
        // Show a minimal hamburger that only navigates Home, for states where the full menu can't be
        // opened yet (loading) but the user still needs an escape hatch off a stuck/poison route.
        homeEscape?: boolean,
        // 'past' greys the header and badges it, so an old trip can't be mistaken for a live one.
        tone?: HeaderTone,
    },
    activeTrip?: Trip,
    activeGuide?: Guide,
    drawerState?: {
        isDrawerOpen: boolean,
        setDrawerOpen: (isOpen: boolean) => unknown,
    },
    ariaLabel: string,
    /** When true (default), content is centred in 50% width with 25% clear on each side on wide screens. */
    centerOnWide?: boolean,
} & PropsWithChildren) {
    const theme = useMantineTheme();
    const colorScheme = useColorScheme();
    const userData = useContext(UserContext);
    const isWide = useIsWide();

    const [ internalIsDrawerOpen, internalSetDrawerOpen ] = useState(false);

    const effectiveDrawerState = props.drawerState
        ?? { isDrawerOpen: internalIsDrawerOpen, setDrawerOpen: internalSetDrawerOpen };

    const shouldCenter = (isWide ?? false) && props.centerOnWide !== false;
    const mainContent = shouldCenter
        ? <div className={shellClasses.wideCenteredMain}>{props.children}</div>
        : props.children;

    const canOpenMenu = props.header?.canOpenMenu ?? true;
    return <AppShell
        className={shellClasses.appshell}
        aria-label={props.ariaLabel}
        padding={0}
        styles={{
            main: {
                backgroundColor: colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
            },
        }}
    >
        { props.header && <TSAppShellHeader
            canOpenMenu={canOpenMenu}
            homeEscape={props.header.homeEscape}
            tone={props.header.tone}
            title={props.header.title}
            isDrawerOpen={effectiveDrawerState.isDrawerOpen}
            setDrawerOpen={effectiveDrawerState.setDrawerOpen}
            rightSide={props.header.rightSide
                ? props.header.rightSide
                : userData.status === 'logged-out' ? <UnauthenticatedRightSide /> : undefined}
        >
            {props.header.bottom && props.header.bottom}
        </TSAppShellHeader> }
        <SheetsMountPoint />
        {mainContent}
        {canOpenMenu && <TravelScrollDrawer
            activeTrip={props.activeTrip}
            activeGuide={props.activeGuide}
            isOpen={effectiveDrawerState.isDrawerOpen}
            setOpen={effectiveDrawerState.setDrawerOpen}
        />}
    </AppShell>;
}

/**
 * Header colour scheme. 'past' is deliberately drab: the point is that a trip you took last year
 * should not look like the one you're planning.
 */
export type HeaderTone = 'default' | 'past';

const HEADER_BACKGROUNDS: Record<HeaderTone, string> = {
    default: 'var(--mantine-color-blue-5)',
    past: 'var(--mantine-color-gray-6)',
};

function TSAppShellHeader(props: {
    canOpenMenu: boolean,
    homeEscape?: boolean,
    tone?: HeaderTone,
    title?: string,
    rightSide?: JSX.Element,
    isDrawerOpen: boolean,
    setDrawerOpen: (isOpen: boolean) => unknown,
} & PropsWithChildren) {
    const router = useRouter();
    // Burger behaviour: open the full drawer when allowed; else, if homeEscape is set, navigate Home;
    // otherwise it's inert.
    const onBurger = props.canOpenMenu
        ? props.setDrawerOpen
        : props.homeEscape
            ? () => router.push('/')
            : () => { };
    const tone = props.tone ?? 'default';
    return <AppShell.Header
        className={shellClasses.header}
        styles={{
            header: {
                backgroundColor: HEADER_BACKGROUNDS[tone],
            },
        }}
    >
        <Group wrap='nowrap' style={{ height: '100%' }} justify='space-between' gap='xs' ml='xs' mr='sm'>
            <Group wrap='nowrap' style={{ height: '100%', overflow: 'hidden', flex: 1 }} justify="left" gap="xs">
                <TravelScrollBurger
                    canOpenMenu={props.canOpenMenu}
                    ariaLabel={!props.canOpenMenu && props.homeEscape ? 'Go Home' : undefined}
                    isOpen={props.canOpenMenu ? props.isDrawerOpen : false}
                    setOpen={onBurger}
                />
                { props.title && <Text fw={600} lineClamp={1} style={{ textOverflow: 'ellipsis', overflow: 'hidden', flex: 1, textAlign: 'left' }} color='white'>
                    {props.title}
                </Text> }
                { tone === 'past' && <Badge
                    variant='filled'
                    color='dark'
                    size='sm'
                    radius='sm'
                    style={{ flexShrink: 0 }}
                    aria-label='Past Trip'
                >
                    Past
                </Badge> }
            </Group>
            { props.rightSide && <Group wrap='nowrap' justify='right'>
                {props.rightSide}
            </Group> }
        </Group>
        {props.children}
    </AppShell.Header>;
}
